import * as React from 'react';
import { Button, InputGroup, ButtonGroup, Tag, Tooltip } from '@blueprintjs/core';
import { useSelector, useDispatch } from 'react-redux';
import { addLogMessage, clearLogMessages } from '../../redux/actions/log-actions';
import { AnsiUp } from 'ansi_up';

// Handle ResizeObserver errors
const handleResizeObserverError = (e) => {
    if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
        e.message.includes('ResizeObserver loop limit exceeded')) {
        // Suppress this specific error as it's harmless
        e.stopImmediatePropagation();
        return false;
    }
    return true;
};

const Output = () => {
    const dispatch = useDispatch();
    const { messages } = useSelector(state => state.log);
    const outputRef = React.useRef(null);
    const ansiUpRef = React.useRef(new AnsiUp()); // Create once, reuse
    const listenerRef = React.useRef(null); // Store listener reference for cleanup
    
    // UI State
    const [searchTerm, setSearchTerm] = React.useState('');
    const [autoScroll, setAutoScroll] = React.useState(true);
    const [isPaused, setIsPaused] = React.useState(false);

    // Auto-scroll to bottom when new messages arrive
    React.useEffect(() => {
        if (autoScroll && outputRef.current && !isPaused) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
        }
    }, [messages, autoScroll, isPaused]);

    // Setup log listener with proper cleanup
    React.useEffect(() => {
        const handleLogAdd = (event, message) => {
            if (isPaused) return; // Don't add messages when paused
            
            let msg = message.message;
            if (message?.format === 'ansi') {
                msg = ansiUpRef.current.ansi_to_html(message.message);
                msg = msg.replace(/\n/g, '<br/>');
            }
            dispatch(addLogMessage(msg));
        };

        // Store reference for cleanup
        listenerRef.current = handleLogAdd;

        // Register listener
        bodapro.listenOn('log.add', handleLogAdd);

        // Setup ResizeObserver error handler
        const errorHandler = (e) => {
            if (!handleResizeObserverError(e)) {
                return;
            }
        };
        window.addEventListener('error', errorHandler);

        // Cleanup function
        return () => {
            if (listenerRef.current) {
                bodapro.removeListener({ 
                    channel: 'log.add', 
                    callback: listenerRef.current 
                });
            }
            window.removeEventListener('error', errorHandler);
        };
    }, [dispatch, isPaused]);

    // Filter messages based on search term
    const filteredMessages = React.useMemo(() => {
        if (!searchTerm) return messages;
        
        const lowerSearch = searchTerm.toLowerCase();
        return messages.filter(msg => {
            // Strip HTML tags for searching
            const textContent = msg.replace(/<[^>]*>/g, '');
            return textContent.toLowerCase().includes(lowerSearch);
        });
    }, [messages, searchTerm]);

    // Handle scroll to detect if user scrolled up (disable auto-scroll)
    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const isAtBottom = Math.abs(scrollHeight - clientHeight - scrollTop) < 10;
        setAutoScroll(isAtBottom);
    };

    // Export logs to file
    const handleExport = () => {
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const content = messages.map(msg => {
            // Strip HTML tags
            return msg.replace(/<[^>]*>/g, '').replace(/<br\/?>/g, '\n');
        }).join('\n');
        
        const blob = new Blob([content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bodapro-logs-${timestamp}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // Clear logs
    const handleClear = () => {
        dispatch(clearLogMessages());
    };

    // Toggle pause
    const handleTogglePause = () => {
        setIsPaused(!isPaused);
    };

    // Scroll to bottom manually
    const handleScrollToBottom = () => {
        if (outputRef.current) {
            outputRef.current.scrollTop = outputRef.current.scrollHeight;
            setAutoScroll(true);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Toolbar */}
            <div 
                style={{ 
                    padding: '8px', 
                    borderBottom: '1px solid #394b59',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                    flexShrink: 0
                }}
            >
                <InputGroup
                    leftIcon="search"
                    placeholder="Search logs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    small
                    style={{ width: '250px' }}
                    rightElement={
                        searchTerm ? (
                            <Button 
                                icon="cross" 
                                minimal 
                                small 
                                onClick={() => setSearchTerm('')}
                            />
                        ) : null
                    }
                />
                
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px', alignItems: 'center' }}>
                    <Tag minimal>
                        {filteredMessages.length} / {messages.length}
                    </Tag>
                    
                    <ButtonGroup minimal>
                        <Tooltip content={isPaused ? "Resume logging" : "Pause logging"}>
                            <Button
                                icon={isPaused ? "play" : "pause"}
                                small
                                onClick={handleTogglePause}
                                intent={isPaused ? "warning" : "none"}
                            />
                        </Tooltip>
                        
                        <Tooltip content="Scroll to bottom">
                            <Button
                                icon="arrow-down"
                                small
                                onClick={handleScrollToBottom}
                                disabled={autoScroll}
                            />
                        </Tooltip>
                        
                        <Tooltip content="Export logs">
                            <Button
                                icon="download"
                                small
                                onClick={handleExport}
                                disabled={messages.length === 0}
                            />
                        </Tooltip>
                        
                        <Tooltip content="Clear logs">
                            <Button
                                icon="trash"
                                small
                                onClick={handleClear}
                                disabled={messages.length === 0}
                            />
                        </Tooltip>
                    </ButtonGroup>
                </div>
            </div>

            {/* Log Output */}
            <div
                ref={outputRef}
                className="p-2"
                onScroll={handleScroll}
                style={{
                    overflowY: "auto",
                    flex: 1,
                    fontFamily: "monospace",
                    fontSize: '12px',
                    lineHeight: '1.4',
                    willChange: "auto",
                    contain: "layout style",
                }}
            >
                {filteredMessages.length === 0 && messages.length > 0 && searchTerm && (
                    <div style={{ color: '#738694', padding: '20px', textAlign: 'center' }}>
                        No logs match "{searchTerm}"
                    </div>
                )}
                
                {filteredMessages.length === 0 && messages.length === 0 && (
                    <div style={{ color: '#738694', padding: '20px', textAlign: 'center' }}>
                        {isPaused ? 'Logging paused' : 'No logs yet'}
                    </div>
                )}
                
                {filteredMessages.map((msg, idx) => (
                    <div
                        key={idx}
                        style={{
                            marginBottom: '2px',
                            wordBreak: 'break-word',
                            padding: '2px 0',
                        }}
                        dangerouslySetInnerHTML={{ __html: msg }}
                    />
                ))}
            </div>
            
            {/* Status Bar */}
            {!autoScroll && (
                <div 
                    style={{ 
                        padding: '4px 8px', 
                        borderTop: '1px solid #394b59',
                        fontSize: '11px',
                        color: '#738694',
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: '#252a31'
                    }}
                    onClick={handleScrollToBottom}
                >
                    ⬇ New messages below - Click to scroll to bottom
                </div>
            )}
        </div>
    );
};

export default Output;